<template>
    <div class="tile max-w-full bg-white">
        <div v-if="success">
            <InlineAlert type="success" class="mb-4">{{
                t('contactForm.successSubmit')
            }}</InlineAlert>
        </div>
        <div v-else>
            <h2 class="text-xl">{{ title }}</h2>
            <p>{{ description }}</p>
            <hr class="my-6 border-t-0 border-b border-black/10" />
            <form class="flex flex-col gap-4" novalidate @submit.prevent="handleSubmit">
                <InlineAlert v-if="generalErrorMessage">
                    {{ generalErrorMessage }}
                </InlineAlert>
                <div>
                    <Radio
                        v-model="form.role"
                        name="occupation"
                        :error-message="formErrors.role"
                        :validation-type="formErrors.role ? 'error' : 'none'"
                        :options="
                            Array.from(Object.entries(occupationOptions)).map(([key, value]) => ({
                                id: value,
                                label: key,
                                value: value,
                            }))
                        "
                        :show-optional-label="false"
                        :label="t('contactForm.whatIsYourOccupationLabel')" />
                    <InlineMessage
                        :message="t('contactForm.whatIsYourOccupationHintText')"
                        class="mt-2" />
                </div>
                <SelectInput
                    v-model="form.question"
                    name="contactReason"
                    :error-message="formErrors.question"
                    :validation-type="formErrors.question ? 'error' : 'none'"
                    :required="true"
                    :label="t('contactForm.whatIsYourReasonForContactLabel')"
                    :data="contactReasons" />

                <SelectInput
                    v-if="showApplicationField"
                    v-model="form.interventionId"
                    :error-message="formErrors.interventionId"
                    :validation-type="formErrors.interventionId ? 'error' : 'none'"
                    name="intervention"
                    :required="true"
                    :label="t('contactForm.whatAppAreYouUsingLabel')"
                    :data="interventionOptions" />

                <template v-if="!isMedicalQuestion">
                    <Text
                        v-if="showOrderNumberField"
                        v-model="form.orderNumber"
                        :error-message="formErrors.orderNumber"
                        :validation-type="formErrors.orderNumber ? 'error' : 'none'"
                        type="text"
                        name="orderNumber"
                        :label="t('contactForm.patientQuestionOrderNumber')"
                        :required="false" />

                    <Text
                        v-model="form.firstName"
                        :error-message="formErrors.firstName"
                        :validation-type="formErrors.firstName ? 'error' : 'none'"
                        type="text"
                        name="firstName"
                        :label="t('form.firstNameLabel')"
                        :required="true" />

                    <Text
                        v-model="form.lastName"
                        :error-message="formErrors.lastName"
                        :validation-type="formErrors.lastName ? 'error' : 'none'"
                        type="text"
                        name="lastName"
                        :label="t('form.lastNameLabel')"
                        :required="true" />

                    <Text
                        v-model="form.companyOrOrganisation"
                        :error-message="formErrors.companyOrOrganisation"
                        :validation-type="formErrors.companyOrOrganisation ? 'error' : 'none'"
                        type="text"
                        name="company"
                        :label="t('form.companyOrOrganisationLabel')"
                        :required="true" />

                    <SelectInput
                        v-if="!hideCountryField"
                        v-model="form.country"
                        name="country"
                        :error-message="formErrors.country"
                        :validation-type="formErrors.country ? 'error' : 'none'"
                        :required="true"
                        :label="t('order.countryLabel')"
                        :data="countries" />

                    <Text
                        v-model="form.emailAddress"
                        :error-message="formErrors.emailAddress"
                        :validation-type="formErrors.emailAddress ? 'error' : 'none'"
                        type="email"
                        name="email"
                        :label="t('form.emailAddressLabel')"
                        :required="true" />

                    <label class="justify-between font-semibold">
                        {{ t('contactForm.messageLabel') }}
                        <textarea
                            id="message"
                            v-model="form.message"
                            name="message"
                            class="border-brand-black w-full rounded border p-2 font-normal"
                            rows="4" />
                    </label>
                    <div
                        v-if="privacyStatement"
                        class="privacy-statement"
                        v-html="privacyStatement" />

                    <ClientOnly>
                        <RecaptchaWidget />
                    </ClientOnly>

                    <button :disabled="loading" type="submit" class="btn-primary-a">
                        {{ t('form.submit') }}
                    </button>
                </template>
                <p v-else>{{ t('contactForm.patientQuestionMedicalQuestionAnswer') }}</p>
            </form>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type {
    ContactRequestModel,
    FormComponent,
    PrescriptionIntervention,
} from '@su4r-frontend/api';
const { title, contactOptions, description, privacyStatement, countries } =
    defineProps<FormComponent>();
const { locale, t } = useI18n();
const logger = useLogger();
const { status } = useAuth();
const { isStaging, isProduction } = useEnvironment();

const success = ref<boolean>(false);
const loading = ref<boolean>(false);
const generalErrorMessage = ref<string>('');

const occupationOptions = {
    Patient: 'patient',
    Practitioner: 'practitioner',
    Developer: 'developer',
    Press: 'press',
};

const interventions = ref<PrescriptionIntervention[]>([]);

const interventionOptions = computed<Record<string, string>>(() => {
    if (!interventions.value) return {};

    const options: Record<string, string> = {};

    for (const intervention of interventions.value) {
        options[intervention.id] = intervention.title;
    }

    return options;
});

const contactReasons = computed<Record<string, string>>(() => {
    const reasons = contactOptions?.find((option) => option.role === form.role)?.contactReason;

    if (!reasons) {
        return {};
    }

    return reasons;
});

const form = reactive({
    role: occupationOptions.Developer,
    firstName: '',
    lastName: '',
    emailAddress: '',
    question: '',
    message: '',
    country: '',
    companyOrOrganisation: '',
    interventionId: '',
    orderNumber: '',
});

const formErrors = reactive({
    role: '',
    firstName: '',
    lastName: '',
    emailAddress: '',
    question: '',
    message: '',
    country: '',
    companyOrOrganisation: '',
    interventionId: '',
    orderNumber: '',
});

const isPatientForm = computed<boolean>(() => {
    return form.role === occupationOptions.Patient;
});

const showApplicationField = computed<boolean>(() => {
    return (
        isPatientForm.value &&
        (form.question === 'questionApplication' || form.question === 'feedbackApplication')
    );
});

const isMedicalQuestion = computed<boolean>(() => {
    return isPatientForm.value && form.question === 'medicalQuestion';
});

const showOrderNumberField = computed<boolean>(() => {
    return isPatientForm.value && form.question === 'orderQuestion';
});

const hideCountryField = computed<boolean>(() => {
    return (
        isPatientForm.value &&
        (form.question === 'platformQuestion' || form.question === 'platformFeedback')
    );
});

async function fetchInterventions(): Promise<void> {
    const url =
        status.value === 'authenticated' ? '/api/interventions/clinic' : '/api/interventions';

    const response = await $fetch(url, {
        query: { culture: locale.value },
    });

    if (response) {
        interventions.value = response;
    }
}

watch(
    () => form.role,
    async (role) => {
        if (role === occupationOptions.Patient && interventions.value.length === 0) {
            await fetchInterventions();
        }
    }
);

function isFormValid(e: Event): boolean {
    if (!e.target) return false;
    const formElement = e.target as HTMLFormElement;

    Array.from(formElement).forEach((el) => {
        const element = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

        if (element.name === 'firstName') {
            if (element.validity.valueMissing) {
                formErrors.firstName = t('form.formErrorRequired');
            }
        }
        if (element.name === 'lastName') {
            if (element.validity.valueMissing) {
                formErrors.lastName = t('form.formErrorRequired');
            }
        }
        if (element.name === 'email') {
            if (element.validity.valueMissing) {
                formErrors.emailAddress = t('form.formErrorRequired');
            }
            if (element.validity.typeMismatch) {
                formErrors.emailAddress = t('form.formErrorInvalidFormat');
            }
        }
        if (element.name === 'contactReason') {
            if (element.validity.valueMissing) {
                formErrors.question = t('form.formErrorRequired');
            }
        }
        if (element.name === 'message') {
            if (element.validity.valueMissing) {
                formErrors.message = t('form.formErrorRequired');
            }
        }
        if (element.name === 'country') {
            if (element.validity.valueMissing && !hideCountryField.value) {
                formErrors.country = t('form.formErrorRequired');
            }
        }
        if (element.name === 'company') {
            if (element.validity.valueMissing) {
                formErrors.companyOrOrganisation = t('form.formErrorRequired');
            }
        }
        if (element.name === 'intervention') {
            if (element.validity.valueMissing && showApplicationField.value) {
                formErrors.interventionId = t('form.formErrorRequired');
            }
        }
        if (element.name === 'orderNumber') {
            if (element.validity.valueMissing && showOrderNumberField.value) {
                formErrors.orderNumber = t('form.formErrorRequired');
            }
        }
        if (element.name === 'occupation') {
            if (element.validity.valueMissing) {
                formErrors.role = t('form.formErrorRequired');
            }
        }
    });

    return Object.values(formErrors).every((error) => error === '');
}

function clearErrorMessages(): void {
    Object.keys(formErrors).forEach((key) => {
        formErrors[key as keyof typeof formErrors] = '';
    });
    generalErrorMessage.value = '';
}

async function handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    success.value = false;
    clearErrorMessages();

    let token = null;
    if (!isFormValid(e)) return;

    if (isStaging || isProduction) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        token = (window as any).grecaptcha.getResponse();
        if (!token) return;
    }

    loading.value = true;

    try {
        const response = await $fetch('/api/contact', {
            method: 'POST',
            body: {
                ...form,
                interventionId: form.interventionId ? form.interventionId : null,
                token: token ? token : undefined,
            } as ContactRequestModel & {
                token: string | undefined;
            },
            query: { culture: locale.value },
        });
        success.value = response.success ?? false;
    } catch (error: unknown) {
        logger.error('Error in contact form', error);
        generalErrorMessage.value = t('contactForm.contactError');
    }

    loading.value = false;
}
</script>

<style>
.privacy-statement a {
    text-decoration: underline;
}
</style>
